import type { Node } from "@react-types/shared";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { type JSX, type ReactNode, useRef, useState } from "react";
import { mergeProps, useFocusRing, useFocusWithin, useHover, useListBox, useOption } from "react-aria";
import { Item, type ListProps, type ListState, useListState } from "react-stately";
import { SymbolIcon } from "widgets";

import style from "./styles/SelectWidget.module.scss";

interface ISelectWidgetProps {
  icon?: MaterialSymbol;
  isUiSmall?: boolean;
  label?: ReactNode;
}

// TODO: As SearchAutosuggest?
export function SelectWidget(props: ListProps<HTMLElement> & ISelectWidgetProps): JSX.Element {
  const state = useListState(props);
  const ref = useRef(null);
  const { listBoxProps, labelProps } = useListBox(props, state, ref);
  const [isFocusWithin, onFocusWithinChange] = useState(false);
  const { focusWithinProps } = useFocusWithin({ onFocusWithinChange });

  return (
    <div className={style.container}>
      <label {...labelProps} className={style.label}>
        {props.label}
      </label>
      <div className={clsx(style.select, { [style.select_focus]: isFocusWithin })} {...focusWithinProps}>
        {props.icon && !props.isUiSmall && <SymbolIcon icon={props.icon} className={style.icon} />}
        <ul {...listBoxProps} ref={ref} className={style.list}>
          {[...state.collection].map((item) => (
            <Option key={item.key} item={item} state={state} />
          ))}
        </ul>
      </div>
    </div>
  );
}

interface IOptionProps {
  item: Node<HTMLElement>;
  state: ListState<HTMLElement>;
}

function Option({ item, state }: IOptionProps): JSX.Element {
  const ref = useRef(null);
  const isDisabled = state.disabledKeys.has(item.key);
  const isSelected = state.selectionManager.isSelected(item.key);
  const { optionProps } = useOption({ key: item.key, isDisabled, isSelected }, state, ref);
  const { isFocusVisible, focusProps } = useFocusRing();
  const { hoverProps, isHovered } = useHover({});

  return (
    <li
      {...mergeProps(optionProps, focusProps, hoverProps)}
      ref={ref}
      className={clsx(style.item, { [style.item_select]: isSelected, [style.item_focus]: isFocusVisible || isHovered })}
    >
      {item.rendered}
    </li>
  );
}

export { Item as SelectItem };

// --- Select All-None ---
interface ISelectAllNoneProps {
  current: number; // -1 deactivate icon too
  max: number;
  onSelectAll: (all: boolean) => void;
  label?: string; // default: "Select"
  isUiSmall?: boolean;
}

export function SelectAllNoneWidget({ current, max, onSelectAll, ...props }: ISelectAllNoneProps): JSX.Element {
  const selectKeyIcon =
    current === 0
      ? ["none", "check_box_outline_blank"]
      : current === max
        ? ["all", "check_box"]
        : ["some", "indeterminate_check_box"];

  return (
    <SelectWidget
      icon={(current >= 0 && selectKeyIcon[1]) || undefined}
      label={props.label ?? "Select"}
      isUiSmall={props.isUiSmall}
      selectionMode={"single"}
      selectedKeys={[selectKeyIcon[0]]}
      onSelectionChange={(selection) => {
        if (selection instanceof Set) {
          if (selection.has("all")) {
            onSelectAll(true);
          } else if (selection.has("none")) {
            onSelectAll(false);
          }
        }
      }}
    >
      <Item key={"all"}>All</Item>
      <Item key={"none"}>None</Item>
    </SelectWidget>
  );
}
